import { List } from 'immutable';
import I18n from 'I18n';
import { FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import { Link } from 'foundations-components/transitional/components/Link';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import { fetchAggregatedDealRollups as fetchAggregatedDealRollupsRequest, fetchAggregatedGoals as fetchAggregatedGoalsRequest, fetchAggregatedSubmissions as fetchAggregatedSubmissionsRequest, fetchPipelineValue as fetchPipelineValueRequest, fetchGoalsBreakdownForOwnerOrTeam as fetchGoalsBreakdownForOwnerOrTeamRequest } from '../api/forecastApi';
import { TOGGLE_IS_AGGREGATED_DEAL_ROLL_UP_FETCHING, TOGGLE_IS_AGGREGATED_GOALS_FETCHING, TOGGLE_IS_AGGREGATED_SUBMISSIONS_FETCHING, AGGREGATED_OVERVIEW_DATA_FETCH_SUCCEEDED, AGGREGATED_OVERVIEW_DATA_FETCH_FAILED, FETCH_AGGREGATED_DEAL_ROLL_UPS_SUCCEEDED, FETCH_AGGREGATED_GOALS_SUCCEEDED, FETCH_AGGREGATED_SUBMISSIONS_SUCCEEDED, FETCH_OPEN_PIPELINE_VALUE_SUCCEEDED, FETCH_AGGREGATED_GOAL_BREAKDOWN, TOGGLE_AGGREGATED_GOAL_BREAKDOWN_FETCHING, FETCH_AGGREGATED_DEAL_ROLL_UPS_FAILED, FETCH_AGGREGATED_SUBMISSIONS_FAILED, FETCH_AGGREGATED_GOALS_FAILED, INITIAL_OVERVIEW_DATA_FETCH_SUCCEEDED } from './ActionTypes';
import { getUsers, getTeams } from './UsersActions';
import { getOwners } from './OwnersActions';
import { fetchPortalConfig } from './ForecastActions';
import { fetchDisplayOptions } from './DisplayOptionsActions';
import { fetchDealsWithForecastCategories } from './DealsWithForecastCategoriesActions';
import { fetchProcessingDealsStatus } from './AutomationActions';
import { setDealSidebarOwnerId } from './OverviewDataActions';
import { fetchProperties } from './PropertiesActions';
import { getHasViewAllScope, getIsUngatedToForecastingMultipleOutcomes, getUserPrimaryTeam } from '../selectors/authSelectors';
import { getCurrentDatePortalTz } from '../selectors/dateSelectors';
import { getIsInitialOverviewFetchSuccessful, getSelectedTeamId } from '../selectors/overviewSelectors';
import { getFilters } from '../selectors/filtersSelectors';
import { getIsAllPipelines, getPortalWideCadence, getPipelines, getSelectedForecastType } from '../selectors/forecastSelectors';
import { getSelectedDetailOwnerId } from '../selectors/detailViewSelectors';
import { checkHasExpectedError, checkHasMultiCurrencyError } from '../utils/apiErrorUtils';
import { getCompanyCurrency } from '../selectors/multiCurrencySelectors';
import { getDealGroupingTypeFilter } from '../selectors/filtersSelectors';
import { FILTERS, CUSTOM_DATE_OPTION } from '../constants/FiltersConstants';
import { OWNER } from '../constants/AssigneeTypes';
import { DEAL_STAGE } from '../constants/DealGroupingType';
import { getTimePeriodOffset, getTimePeriodOption } from '../utils/dateUtils';
import { refreshPage } from '../utils/locationUtils';
import { logRavenException } from 'ui-deal-management-components-lib/utils/ravenWrapperUtil';
import { fetchHasPortalSeenFmoAdminOnboarding } from '../actions/PortalSettingsActions';
import { getTimePeriodFilter } from '../selectors/filtersSelectors';
import { jsx as _jsx } from "react/jsx-runtime";
function buildRequestData(state) {
  var _getSelectedForecastT;
  const selectedTeamId = getSelectedTeamId(state);
  const isDealStage = getDealGroupingTypeFilter(state) === DEAL_STAGE;
  const filters = getFilters(state);
  const timePeriod = getTimePeriodFilter(state);
  const pipelines = getPipelines(state);
  const cadence = getPortalWideCadence(state);
  const isAllPipelines = getIsAllPipelines(state);
  const localDateNow = getCurrentDatePortalTz(state);
  const pipelineIds = pipelines.map(pipeline => pipeline.get('pipelineId')).toSet();

  // filters existing pipeline ids
  let pipeline = filters.get(FILTERS.PIPELINE).toSet().intersect(pipelineIds).toList();

  // if no valid pipeline ids in filters, use first pipeline id
  if (!pipeline.size) {
    pipeline = List([pipelineIds.first()]);
  }

  // for an edge case where pipelines hasn't populated in store yet
  if (!pipelineIds.size) {
    pipeline = List();
  }

  // for edge case where multiple pipelines selected but on deal stage
  if (isDealStage && pipeline.size > 1) {
    pipeline = List([pipeline.first()]);
  }
  const teamIds = filters.get(FILTERS.TEAM);
  const timePeriodOption = getTimePeriodOption(timePeriod);
  const timePeriodOffset = getTimePeriodOffset(timePeriod);
  const isCustomPeriod = timePeriodOption === CUSTOM_DATE_OPTION;
  // @ts-expect-error isCustomPeriod true implicitly means timePeriod is not a string. The field is overloaded.
  const customStartDate = isCustomPeriod ? timePeriod.get('startDate') : null;
  // @ts-expect-error isCustomPeriod true implicitly means timePeriod is not a string. The field is overloaded.
  const customEndDate = isCustomPeriod ? timePeriod.get('endDate') : null;
  const selectedDetailOwnerId = getSelectedDetailOwnerId(state);
  const isFmoEnabled = getIsUngatedToForecastingMultipleOutcomes(state);
  const selectedForecastTypeId = isFmoEnabled ? (_getSelectedForecastT = getSelectedForecastType(state)) === null || _getSelectedForecastT === void 0 ? void 0 : _getSelectedForecastT.forecastTypeId : undefined;
  if (selectedDetailOwnerId) {
    return Object.assign({
      pipeline,
      timePeriod: {
        timePeriodOption,
        timePeriodOffset,
        customStartDate,
        customEndDate
      },
      selectedOwnerId: selectedDetailOwnerId,
      localDateNow,
      assigneeType: OWNER,
      // hardcoded for now until we support team goals in the future
      pipelineCadence: cadence,
      isGroupedByStage: isDealStage,
      isAllPipelines
    }, isFmoEnabled ? {
      forecastTypeId: selectedForecastTypeId
    } : {});
  }
  return Object.assign({
    pipeline,
    timePeriod: {
      timePeriodOption,
      timePeriodOffset,
      customStartDate,
      customEndDate
    },
    teamIds,
    selectedTeamId,
    localDateNow,
    assigneeType: OWNER,
    pipelineCadence: cadence,
    isTeamView: true,
    isGroupedByStage: isDealStage,
    isAllPipelines
  }, isFmoEnabled ? {
    forecastTypeId: selectedForecastTypeId
  } : {});
}
function buildRequestDataDealSidebar(state, isTeam, ownerId) {
  var _getSelectedForecastT2;
  const filters = getFilters(state);
  const timePeriod = getTimePeriodFilter(state);
  const pipelines = filters.get(FILTERS.PIPELINE);
  const isDealStage = getDealGroupingTypeFilter(state) === DEAL_STAGE;
  const localDateNow = getCurrentDatePortalTz(state);
  const timePeriodOption = getTimePeriodOption(timePeriod);
  const timePeriodOffset = getTimePeriodOffset(timePeriod);
  const isCustomPeriod = timePeriodOption === CUSTOM_DATE_OPTION;
  // @ts-expect-error isCustomPeriod true implicitly means timePeriod is not a string. The field is overloaded.
  const customStartDate = isCustomPeriod ? timePeriod.get('startDate') : null;
  // @ts-expect-error isCustomPeriod true implicitly means timePeriod is not a string. The field is overloaded.
  const customEndDate = isCustomPeriod ? timePeriod.get('endDate') : null;
  const portalForecastCadence = getPortalWideCadence(state);
  const isAllPipelines = getIsAllPipelines(state);
  const isFmoEnabled = getIsUngatedToForecastingMultipleOutcomes(state);
  const selectedForecastTypeId = isFmoEnabled ? (_getSelectedForecastT2 = getSelectedForecastType(state)) === null || _getSelectedForecastT2 === void 0 ? void 0 : _getSelectedForecastT2.forecastTypeId : undefined;
  if (isTeam) {
    return Object.assign({}, isAllPipelines && {
      isAllPipelines
    }, !isAllPipelines && {
      pipeline: pipelines
    }, {
      timePeriod: {
        timePeriodOption,
        timePeriodOffset,
        customStartDate,
        customEndDate
      },
      selectedTeamId: ownerId,
      localDateNow,
      portalForecastCadence,
      isTeamView: isTeam,
      isGroupedByStage: isDealStage
    }, isFmoEnabled ? {
      forecastTypeId: selectedForecastTypeId
    } : {});
  }
  return Object.assign({}, isAllPipelines && {
    isAllPipelines
  }, !isAllPipelines && {
    pipeline: pipelines
  }, {
    timePeriod: {
      timePeriodOption,
      timePeriodOffset,
      customStartDate,
      customEndDate
    },
    selectedOwnerId: ownerId,
    localDateNow,
    portalForecastCadence,
    isTeamView: isTeam,
    isGroupedByStage: isDealStage
  }, isFmoEnabled ? {
    forecastTypeId: selectedForecastTypeId
  } : {});
}
export function toggleAggregatedDealRollupFetching(isFetching) {
  return {
    type: TOGGLE_IS_AGGREGATED_DEAL_ROLL_UP_FETCHING,
    payload: isFetching
  };
}
export function toggleAggregatedGoalsFetching(isFetching) {
  return {
    type: TOGGLE_IS_AGGREGATED_GOALS_FETCHING,
    payload: isFetching
  };
}
export function toggleAggregatedSubmissionsFetching(isFetching) {
  return {
    type: TOGGLE_IS_AGGREGATED_SUBMISSIONS_FETCHING,
    payload: isFetching
  };
}
export const succeedAggregatedOverviewDataFetch = () => ({
  type: AGGREGATED_OVERVIEW_DATA_FETCH_SUCCEEDED
});
export const failAggregatedOverviewDataFetch = () => ({
  type: AGGREGATED_OVERVIEW_DATA_FETCH_FAILED
});
export function fetchInitialOverviewData() {
  return (dispatch, getState) => {
    const state = getState();
    const canViewAll = getHasViewAllScope(state);
    const userPrimaryTeam = getUserPrimaryTeam(state);
    const isInitialOverviewFetchSuccessful = getIsInitialOverviewFetchSuccessful(state);
    const isFmoEnabled = getIsUngatedToForecastingMultipleOutcomes(state);
    if (isInitialOverviewFetchSuccessful) {
      return Promise.resolve();
    }
    return Promise.all([dispatch(getUsers()), dispatch(getTeams({
      canViewAll,
      userPrimaryTeamId: userPrimaryTeam === null || userPrimaryTeam === void 0 ? void 0 : userPrimaryTeam.id
    })), dispatch(getOwners()), dispatch(fetchProcessingDealsStatus()), dispatch(fetchDealsWithForecastCategories()), dispatch(fetchPortalConfig()), ...(isFmoEnabled ? [dispatch(fetchHasPortalSeenFmoAdminOnboarding())] : []), dispatch(fetchDisplayOptions()), dispatch(fetchProperties())]).then(() => dispatch({
      type: INITIAL_OVERVIEW_DATA_FETCH_SUCCEEDED
    })).catch(error => {
      logRavenException(error, 'Initial overview data fetch');
    });
  };
}
export function getAggregatedDealRollups() {
  return (dispatch, getState) => {
    const data = buildRequestData(getState());
    const companyCurrency = getCompanyCurrency(getState());
    dispatch(toggleAggregatedDealRollupFetching(true));
    return fetchAggregatedDealRollupsRequest(data).then(dealRollups => dispatch({
      type: FETCH_AGGREGATED_DEAL_ROLL_UPS_SUCCEEDED,
      payload: {
        data: dealRollups,
        companyCurrency
      }
    })).catch(error => {
      if (checkHasExpectedError(error)) {
        dispatch({
          type: FETCH_AGGREGATED_DEAL_ROLL_UPS_SUCCEEDED,
          payload: {
            companyCurrency
          }
        });
        return;
      }
      if (checkHasMultiCurrencyError(error)) {
        FloatingAlertStore.addAlert({
          message: /*#__PURE__*/_jsx(FormattedJSXMessage, {
            message: "repOverviewTable.fetchMultiCurrencyErrorText_jsx",
            options: {
              onClick: refreshPage
            },
            elements: {
              Link
            }
          }),
          titleText: I18n.text('repOverviewTable.fetchMultiCurrencyErrorTitle'),
          type: 'danger'
        });
      }
      dispatch({
        type: FETCH_AGGREGATED_DEAL_ROLL_UPS_FAILED
      });
      logRavenException(error, 'Aggregated deal rollup fetch', data);
    }).finally(() => {
      dispatch(toggleAggregatedDealRollupFetching(false));
    });
  };
}
export function getOpenPipelineValue() {
  return (dispatch, getState) => {
    var _getSelectedForecastT3;
    const state = getState();
    const companyCurrency = getCompanyCurrency(state);
    const data = buildRequestData(state);
    const pipelinesDataReady = !!data.pipeline;

    // in some cases, on first load, pipelines data takes a while to populate in the store
    // ref: https://sentry.hubteam.com/sentry/forecasting-ui/issues/19404610/
    // this is an attempt to fix this sentry by not making the request
    if (!pipelinesDataReady) {
      return Promise.resolve();
    }
    const pipeline = data.pipeline.toIndexedSeq().toArray();
    const {
      isAllPipelines,
      timePeriod,
      selectedTeamId
    } = data;
    const isFmoEnabled = getIsUngatedToForecastingMultipleOutcomes(state);
    const selectedForecastTypeId = isFmoEnabled ? (_getSelectedForecastT3 = getSelectedForecastType(state)) === null || _getSelectedForecastT3 === void 0 ? void 0 : _getSelectedForecastT3.forecastTypeId : undefined;
    return fetchPipelineValueRequest(Object.assign({
      isAllPipelines,
      pipeline,
      timePeriod,
      selectedTeamId
    }, isFmoEnabled ? {
      forecastTypeId: selectedForecastTypeId
    } : {})).then(response => {
      dispatch({
        type: FETCH_OPEN_PIPELINE_VALUE_SUCCEEDED,
        payload: {
          currencyCode: response.currencyCode || companyCurrency,
          value: response.amount
        }
      });
    }).catch(error => {
      dispatch({
        type: FETCH_OPEN_PIPELINE_VALUE_SUCCEEDED,
        // Todo: confirm this behavior
        payload: null
      });
      if (!checkHasExpectedError(error)) {
        return Promise.resolve();
      }
      logRavenException(error, 'Fetch Pipeline Value', data);
    });
  };
}
export function getAggregatedGoals() {
  return (dispatch, getState) => {
    const data = buildRequestData(getState());
    dispatch(toggleAggregatedGoalsFetching(true));
    return fetchAggregatedGoalsRequest(data).then(goals => {
      const companyCurrency = getCompanyCurrency(getState());
      dispatch({
        type: FETCH_AGGREGATED_GOALS_SUCCEEDED,
        payload: {
          data: goals,
          companyCurrency
        }
      });
    }).catch(error => {
      if (checkHasExpectedError(error)) {
        const companyCurrency = getCompanyCurrency(getState());
        dispatch({
          type: FETCH_AGGREGATED_GOALS_SUCCEEDED,
          payload: {
            companyCurrency
          }
        });
        return;
      }
      dispatch({
        type: FETCH_AGGREGATED_GOALS_FAILED
      });
      logRavenException(error, 'Aggregated goals fetch', data);
    }).finally(() => dispatch(toggleAggregatedGoalsFetching(false)));
  };
}
export function getAggregatedSubmissions() {
  return (dispatch, getState) => {
    const state = getState();
    const data = buildRequestData(state);
    const companyCurrency = getCompanyCurrency(getState());
    dispatch(toggleAggregatedSubmissionsFetching(true));
    if (!data.pipelineCadence) {
      return null;
    }
    return fetchAggregatedSubmissionsRequest(data).then(submissions => {
      dispatch({
        type: FETCH_AGGREGATED_SUBMISSIONS_SUCCEEDED,
        payload: {
          companyCurrency,
          data: submissions
        }
      });
    }).catch(error => {
      if (checkHasExpectedError(error)) {
        dispatch({
          type: FETCH_AGGREGATED_SUBMISSIONS_SUCCEEDED,
          payload: {
            companyCurrency
          }
        });
        return;
      }
      dispatch({
        type: FETCH_AGGREGATED_SUBMISSIONS_FAILED
      });
      logRavenException(error, 'Aggregated submissions fetch', data);
    }).finally(() => dispatch(toggleAggregatedSubmissionsFetching(false)));
  };
}
export function fetchAggregatedOverviewData() {
  return dispatch => Promise.all([dispatch(getAggregatedDealRollups()), dispatch(getAggregatedGoals()), dispatch(getAggregatedSubmissions())]).then(() => dispatch(getOpenPipelineValue())).then(() => dispatch(succeedAggregatedOverviewDataFetch())).catch(() => dispatch(failAggregatedOverviewDataFetch()));
}
function toggleAggregatedGoalBreakdownFetching(isFetching) {
  return {
    type: TOGGLE_AGGREGATED_GOAL_BREAKDOWN_FETCHING,
    payload: isFetching
  };
}
function fetchAggregatedGoalBreakdownAction(data, companyCurrency) {
  return {
    type: FETCH_AGGREGATED_GOAL_BREAKDOWN,
    payload: {
      data,
      companyCurrency
    }
  };
}
export function fetchAggregatedGoalBreakdown(isTeam, ownerId) {
  return (dispatch, getState) => {
    dispatch(toggleAggregatedGoalBreakdownFetching(true));
    const state = getState();
    const data = buildRequestDataDealSidebar(state, isTeam, ownerId);
    const companyCurrency = getCompanyCurrency(state);
    return fetchGoalsBreakdownForOwnerOrTeamRequest(data).then(results => dispatch(fetchAggregatedGoalBreakdownAction(results, companyCurrency))).catch(() => {
      FloatingAlertStore.addAlert({
        message: I18n.text('wizard.alerts.saveError.message'),
        titleText: I18n.text('dealSidebar.goalsFetchingError'),
        type: 'danger'
      });
      dispatch(setDealSidebarOwnerId({
        dealSidebarId: null
      }));
    }).finally(() => dispatch(toggleAggregatedGoalBreakdownFetching(false)));
  };
}